import React, {useRef} from 'react'
import mark from '../../../images/landing/hl-mark.svg'
import wordmarkWhite from '../../../images/landing/hl-wordmark-white.svg'
import avatar2 from '../../../images/landing/avatar-2.png'
import docPage from '../../../images/landing/doc-page.png'
import {BEATS, PHOTOS, deriveDemoState} from './demoState'
import {useDemoClock} from './useDemoClock'
import './heroDemo.css'

const src = asset => (asset && asset.default) || asset
const MARK = src(mark)
const WORDMARK = src(wordmarkWhite)
const AVATAR = src(avatar2)
const DOC = src(docPage)

function Icon({size = 17, children}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  )
}

function FileIcon({size = 17}) {
  return (
    <Icon size={size}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </Icon>
  )
}

function Chevron({size = 14}) {
  return (
    <Icon size={size}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  )
}

const NAV = [
  {label: 'Listing feed', icon: <Icon><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></Icon>},
  {label: 'Disclosure package', icon: <FileIcon />},
  {label: 'Offers', icon: <Icon><path d="M12 2v20" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Icon>, badge: true},
  {label: 'Documents', icon: <Icon><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" /></Icon>},
  {label: 'Messages', icon: <Icon><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>},
]

function ListingRow({photo, name, pill, pillStyle, address, meta}) {
  return (
    <div className="hd-list-row">
      <div className="hd-photo" style={{backgroundImage: `url(${photo})`}} />
      <div>
        <div className="hd-list-title">
          {name}
          <span className="hd-pill" style={pillStyle}>{pill}</span>
        </div>
        <div className="hd-addr">{address}</div>
        <div className="hd-meta-row">{meta}</div>
      </div>
      <div className="hd-quick">
        Quick view
        <Icon size={16}><path d="m6 9 6 6 6-6" /></Icon>
      </div>
    </div>
  )
}

// Sizes the 1440px stage as soon as the HTML is parsed. The production CSS
// minifier cannot divide two lengths, and container-query units never applied
// under the /hlm proxy, so the demo stayed at scale 0 until (and unless) React ran.
const sizeHeroDemo = `(function(){
  var stage = document.currentScript && document.currentScript.previousElementSibling
  if (!stage) return
  var frame = stage.querySelector('.hd-frame')
  function size() {
    var width = stage.clientWidth
    if (!width || !frame) return
    var scale = Math.min(1, width / 1440)
    stage.style.height = Math.round(900 * scale) + 'px'
    frame.style.transform = 'scale(' + scale + ')'
  }
  size()
  window.addEventListener('resize', size)
})()`

function HeroDemo() {
  const {t, paused, scale, stageRef, toggle, seek} = useDemoClock()
  const prevT = useRef(t)
  const cut = t < prevT.current
  prevT.current = t
  const s = deriveDemoState(t)
  const stageStyle = scale == null ? undefined : {height: Math.round(900 * scale)}
  const frameStyle = scale == null ? undefined : {transform: `scale(${scale})`}

  return (
    <div>
      <div ref={stageRef} className="hd-stage" style={stageStyle}>
        <div className={`hd-frame${cut ? ' hd-cut' : ''}`} style={frameStyle} aria-hidden="true">
          <ShareModal s={s} />
          <TopBar />
          <div className="hd-body">
            <Sidebar s={s} />
            <main className="hd-main">
              <Dashboard s={s} />
              <Listing s={s} />
              <div
                className="hd-toast"
                style={{opacity: s.toastOp, transform: `translate(-50%, ${s.toastTy}px)`}}>
                <i>
                  <Icon size={12}>
                    <path d="M20 6 9 17l-5-5" stroke="#fff" strokeWidth="3" />
                  </Icon>
                </i>
                Package shared with 1 buyer agent
              </div>
            </main>
          </div>
          <CreateModal s={s} />
        </div>
      </div>
      <script dangerouslySetInnerHTML={{__html: sizeHeroDemo}} />

      <div className="hlm-hero-chapters">
        <button type="button" className="hlm-chapter-play" aria-label={paused ? 'Play demo' : 'Pause demo'} onClick={toggle}>
          {paused ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{marginLeft: 2}}>
              <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5z" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          )}
        </button>
        {BEATS.map((beat, index) => {
          const active = index === s.cur
          const pct = index < s.cur ? 100 : active ? Math.min(100, (s.lt / (beat.end - beat.start)) * 100) : 0
          return (
            <button
              key={beat.label}
              type="button"
              className={`hlm-chapter-chip${active ? ' is-active' : ''}`}
              onClick={() => seek(beat.start)}>
              <span className="hlm-chapter-num">{index + 1}</span>
              {beat.label}
              <span className="hlm-chapter-progress" style={{width: `${pct}%`}} />
            </button>
          )
        })}
      </div>
    </div>
  )
}

function TopBar() {
  return (
    <div className="hd-topbar">
      <div className="hd-lockup">
        <img className="hd-mark" src={MARK} alt="" />
        <span className="hd-lockup-copy">
          <img className="hd-word" src={WORDMARK} alt="" />
          <span>Listing Management</span>
        </span>
      </div>
      <div className="hd-user">
        <img src={AVATAR} alt="" />
        <div>
          <strong>Alex Agent</strong>
          <span>alex.agent@example.com</span>
        </div>
      </div>
    </div>
  )
}

function Sidebar({s}) {
  return (
    <aside className="hd-side">
      <div className="hd-nav-item" style={{background: s.navDashBg, color: s.navDashFg}}>
        <Icon>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5.5 9.5V21h13V9.5" />
        </Icon>
        <span>Dashboard</span>
        <Icon size={15}>
          <path d="m18 15-6-6-6 6" stroke="#64748b" />
        </Icon>
      </div>
      <div className="hd-sub">
        <div className="hd-sub-open" style={{maxHeight: s.subNavH, opacity: s.listingOp}}>
          <div className="hd-listing-mini">
            <span className="hd-thumb" style={{width: 30, height: 30, backgroundImage: `url(${PHOTOS.main})`}} />
            <span>
              <strong>123 Main Street</strong>
              <em>Open</em>
            </span>
          </div>
          {NAV.map((item, index) => (
            <div key={item.label} className="hd-sub-link" style={{background: s.nav[index].bg, color: s.nav[index].fg}}>
              {item.icon}
              {item.label}
              {item.badge ? <span className="hd-badge" style={{opacity: s.badgeOp}}>3</span> : null}
            </div>
          ))}
        </div>
        <div className="hd-recent-wrap">
          <span className="hd-kicker">Recent listings</span>
          {[
            ['789 Elm Street', PHOTOS.elm],
            ['456 Oak Avenue', PHOTOS.oak],
            ['101 Maple Lane', PHOTOS.maple],
          ].map(([name, photo]) => (
            <div className="hd-recent" key={name}>
              <span className="hd-thumb" style={{width: 30, height: 30, backgroundImage: `url(${photo})`}} />
              <span>
                <strong>{name}</strong>
                <em>Open</em>
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="hd-nav-item" style={{marginTop: 14}}>
        <Icon size={17}>
          <path d="m3 11 18-5v12L3 14v-3z" />
          <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
        </Icon>
        Listing Studio
        <span className="hd-soon">SOON</span>
      </div>
      <div className="hd-side-rule" />
      <span className="hd-kicker">Buy side</span>
      <div className="hd-nav-item">
        <Icon>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </Icon>
        <span>For buyers</span>
        <Icon size={15}>
          <path d="m6 9 6 6 6-6" stroke="#64748b" />
        </Icon>
      </div>
      <div className="hd-collapse">
        <Icon size={14}>
          <path d="m15 18-6-6 6-6" />
        </Icon>
        Collapse
      </div>
    </aside>
  )
}

function Dashboard({s}) {
  return (
    <div className="hd-layer hd-dash hd-ease" style={{opacity: s.dashOp}}>
      <div className="hd-dash-head">
        <div>
          <h3>Good morning, Alex</h3>
          <p>3 active listings.</p>
        </div>
        <div className="hd-sky-btn">
          <Icon size={16}><path d="M12 5v14M5 12h14" /></Icon>
          New listing
        </div>
      </div>
      <div className="hd-metrics">
        <div className="hd-metric">
          <span>Total closings</span>
          <strong>11</strong>
          <em>$12.4M volume</em>
        </div>
        <div className="hd-metric">
          <span>Under contract</span>
          <strong>4</strong>
          <em>$6.1M projected</em>
        </div>
      </div>
      <section className="hd-card">
        <div className="hd-card-head">
          <strong>Your listings</strong>
          <div className="hd-filters">
            <div className="hd-input">Search address</div>
            <div className="hd-select">Days on market</div>
          </div>
        </div>
        <ListingRow
          photo={PHOTOS.oak}
          name="456 Oak Avenue"
          pill="Pending"
          pillStyle={{background: '#fef3c7', color: '#92400e'}}
          address="Anytown, CA 00000 · $1,250,000 · 3 bd / 2 ba"
          meta={
            <>
              <span>41 days on market</span>
              <b style={{color: '#1192e5'}}>22 viewers</b>
              <b>1 offer</b>
              <b style={{color: '#64748b'}}>No messages</b>
            </>
          }
        />
        <ListingRow
          photo={PHOTOS.elm}
          name="789 Elm Street"
          pill="Active"
          pillStyle={{background: '#dcfce7', color: '#15803d'}}
          address="Anytown, CA 00000 · $685,000 · 4 bd / 3 ba"
          meta={
            <>
              <span>9 days on market</span>
              <b style={{color: '#1192e5'}}>11 viewers</b>
              <b style={{color: '#b45309'}}>2 offers</b>
              <b style={{color: '#b45309'}}>1 unread</b>
            </>
          }
        />
        <ListingRow
          photo={PHOTOS.maple}
          name="101 Maple Lane"
          pill="Coming soon"
          pillStyle={{background: '#e8f5ff', color: '#0f6da8'}}
          address="Anytown, CA 00000 · $2,150,000 · 5 bd / 4 ba"
          meta={
            <>
              <span>Not on market yet</span>
              <b style={{color: '#1192e5'}}>0 viewers</b>
              <b>0 offers</b>
              <b style={{color: '#64748b'}}>No messages</b>
            </>
          }
        />
      </section>
    </div>
  )
}

function Listing({s}) {
  return (
    <div className="hd-layer hd-ease" style={{display: 'flex', flexDirection: 'column', opacity: s.listingOp, transform: `translateY(${s.listingTy}px)`}}>
      <div className="hd-listing-head">
        <div className="hd-crumb">
          Dashboard <Chevron /> 123 Main Street
          <span style={{display: 'flex', alignItems: 'center', gap: 8, opacity: s.crumbOp, transition: 'opacity 250ms'}}>
            <Chevron />
            <b>{s.crumb}</b>
          </span>
        </div>
        <div className="hd-listing-row">
          <div className="hd-listing-id">
            <span className="hd-thumb" style={{width: 76, height: 76, borderRadius: 8, backgroundImage: `url(${PHOTOS.main})`}} />
            <div>
              <h3>
                123 Main Street
                <span className="hd-open"><i />Open</span>
              </h3>
              <p>Anytown, CA 00000 · $1,800,000 · 4 bed, 4 bath, 3,240 sq ft</p>
            </div>
          </div>
          <div className="hd-head-actions">
            <div className="hd-ghost">
              <Icon size={16}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Icon>
              Edit info
            </div>
            <div className="hd-ghost">
              <Icon size={16}><path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" /></Icon>
              Messages
            </div>
            <div className="hd-icon-btn">
              <Icon size={18}>
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1l2.1-2.1M17 7l2.1-2.1" />
              </Icon>
            </div>
          </div>
        </div>
      </div>
      <div className="hd-panels">
        <Overview s={s} />
        <Disclosures s={s} />
        <Offers s={s} />
      </div>
    </div>
  )
}

function Overview({s}) {
  const c = s.counts
  return (
    <div className="hd-layer hd-overview hd-ease" style={{opacity: s.overview.op, transform: `translateY(${s.overview.ty}px)`}}>
      <div className="hd-overview-main">
        <div className="hd-sum-grid">
          <div className="hd-sum">
            <div className="hd-sum-h">
              <span className="hd-sum-ico" style={{background: '#e8f5ff', color: '#0f6da8'}}><FileIcon size={15} /></span>
              <strong>Disclosures</strong>
              <span className="hd-ghost" style={{height: 28, fontSize: 12}}>Share</span>
            </div>
            <div className="hd-sum-body">
              <h4>Package shared</h4>
              <div className="hd-sum-line"><span>Pending requests</span><b>{c.pending}</b></div>
              <div className="hd-sum-line"><span>Buyer parties</span><b>{c.parties}</b></div>
              <div className="hd-sum-line"><span>Engaged buyers</span><b>{c.engaged}</b></div>
            </div>
          </div>
          <div className="hd-sum">
            <div className="hd-sum-h">
              <span className="hd-sum-ico" style={{background: '#f0fdf4', color: '#15803d'}}>
                <Icon size={15}><path d="M12 2v20" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Icon>
              </span>
              <strong>Offers</strong>
            </div>
            <div className="hd-sum-body">
              <h4>{c.offers} active offers</h4>
              <div className="hd-sum-line"><span>Highest net proceeds</span><b>{c.net}</b></div>
              <div className="hd-sum-line"><span>New & unread</span><b>{c.unread}</b></div>
              <div className="hd-sum-line"><span>Expiring soon</span><b>0</b></div>
            </div>
          </div>
          <div className="hd-sum">
            <div className="hd-sum-h">
              <span className="hd-sum-ico" style={{background: '#f1f5f9', color: '#475569'}}>
                <Icon size={15}><path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></Icon>
              </span>
              <strong>Listing Studio</strong>
            </div>
            <div className="hd-sum-body">
              <h4>Coming soon</h4>
              <div style={{fontSize: 13, lineHeight: 1.45, color: '#737373'}}>Turn this listing into social posts, flyers, and email in a few clicks.</div>
            </div>
          </div>
        </div>
        <section className="hd-card" style={{overflow: 'hidden'}}>
          <div className="hd-activity-h">
            <div>
              <strong>Recent activity</strong>
              <p>Everything that has happened on this listing, newest first.</p>
            </div>
            <div className="hd-chips">
              <span className="hd-chip is-on"><i style={{background: '#273653'}} />All</span>
              <span className="hd-chip"><i style={{background: '#46b6ff'}} />Disclosures</span>
              <span className="hd-chip"><i style={{background: '#22c55e'}} />Offers</span>
              <span className="hd-chip" style={{color: '#94a3b8'}}><i style={{background: '#cbd5e1'}} />Studio</span>
            </div>
          </div>
          {s.feedRows.map(row => (
            <div key={row.what} className="hd-feed hd-row-ease" style={{opacity: row.op, transform: `translateY(${row.ty}px)`}}>
              <span className="hd-dot" style={{background: row.dot}} />
              <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                <b>{row.who}</b> {row.what}
              </span>
              <em>{row.when}</em>
              <Chevron />
            </div>
          ))}
        </section>
      </div>
      <aside className="hd-details">
        <div className="hd-details-h">
          Listing details
          <span className="hd-link">
            <Icon size={13}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Icon>
            Edit
          </span>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          {s.detailRows.map(row => (
            <div key={row.k} className="hd-kv hd-row-ease" style={{opacity: row.op, transform: `translateX(${row.ty}px)`}}>
              <span>{row.k}</span>
              <b>{row.v}</b>
            </div>
          ))}
        </div>
        <div className="hd-rule" />
        <div className="hd-details-h">
          Parties
          <span className="hd-link">Add</span>
        </div>
        <div className="hd-party">
          <img src={AVATAR} alt="" />
          <span>
            <strong>Alex Agent</strong>
            <span>Admin</span>
          </span>
        </div>
        <div className="hd-rule" />
        <div className="hd-details-h">
          Title & escrow <span className="hd-new">New</span>
        </div>
        <div className="hd-note">Get net seller proceeds estimated by HomeLight Title & Escrow, based on this listing’s price and terms.</div>
        <div className="hd-sky-btn" style={{marginTop: 14, justifyContent: 'center'}}>Generate net sheet</div>
        <div style={{marginTop: 10, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#1192e5'}}>See a sample</div>
      </aside>
    </div>
  )
}

function Disclosures({s}) {
  const tab = (style, label, icon, extra) => (
    <div className="hd-tab" style={{color: style.fg, background: style.bg, boxShadow: style.sh}}>
      {icon}
      {label}
      {extra}
    </div>
  )
  return (
    <div className="hd-layer hd-disc hd-ease" style={{opacity: s.disclosures.op, transform: `translateY(${s.disclosures.ty}px)`}}>
      <div className="hd-disc-head">
        <div>
          <h3>Disclosure package</h3>
          <p>{s.discStatus}</p>
        </div>
        <div style={{display: 'flex', gap: 10}}>
          <div className="hd-ghost" style={{height: 36, fontSize: 13}}>Copy link</div>
          <div className="hd-sky-btn" style={{height: 36, fontSize: 13, background: s.shareBg, transform: `translateY(${s.shareTy}px)`, transition: 'background 150ms'}}>
            Share package
          </div>
        </div>
      </div>
      <div className="hd-tabs">
        {tab(s.tabs.docs, 'Package documents', <FileIcon size={15} />)}
        {tab(s.tabs.ai, 'AI summary', <Icon size={15}><path d="M12 4l1.7 4.6L18 10l-4.3 1.4L12 16l-1.7-4.6L6 10l4.3-1.4z" stroke="#7b61ff" /></Icon>)}
        {tab(
          s.tabs.viewers,
          'Viewers',
          <Icon size={15}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7" /><circle cx="12" cy="12" r="3" /></Icon>,
          <span className="hd-count-pill" style={{background: s.tabs.viewersPillBg, color: s.tabs.viewersPillFg}}>{s.viewerCount}</span>,
        )}
        {tab(s.tabs.activity, 'Activity', <Icon size={15}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></Icon>)}
      </div>
      <div style={{flex: 1, position: 'relative', minHeight: 0}}>
        <div className="hd-layer" style={{display: 'flex', flexDirection: 'column', gap: 12, opacity: s.docsOp, transition: 'opacity 300ms'}}>
          <div className="hd-doc-actions">
            <div className="hd-mini is-sky">Add documents</div>
            <div className="hd-mini is-line">Download</div>
            <div className="hd-mini is-line">Reorder</div>
            <div className="hd-mini is-line">Send to DocuSign</div>
          </div>
          {s.docRows.map(doc => (
            <div key={doc.name} className="hd-doc hd-row-ease" style={{opacity: doc.op, transform: `translateY(${doc.ty}px)`}}>
              <span className="hd-check" />
              <div className="hd-doc-card">
                <div className="hd-doc-thumb" style={{backgroundImage: `url(${doc.doc ? DOC : doc.thumb})`}} />
                <div>
                  <strong>{doc.name}</strong>
                  <span>{doc.meta}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="hd-layer hd-viewers" style={{opacity: s.viewersOp, transition: 'opacity 300ms'}}>
          <div>
            <h3>Viewers ({s.viewerCount})</h3>
            <p>Engagement scoring is on — viewers are ranked by likelihood to submit an offer.</p>
          </div>
          <div className="hd-tiles">
            {[
              ['hot', '#dc2626', 'Hot', 'Likely to offer this week'],
              ['warm', '#ea580c', 'Warm', 'Engaged, not yet near offer'],
              ['interested', '#d9a441', 'Interested', 'Showing early interest'],
              ['cool', '#46b6ff', 'Cool', 'Browsing or low-intent'],
            ].map(([key, color, label, note]) => (
              <div className="hd-tile" key={key} style={{borderLeftColor: color}}>
                <strong style={{color}}>{s.tiles[key]}</strong>
                <em style={{color}}>{label}</em>
                <span>{note}</span>
              </div>
            ))}
          </div>
          <div className="hd-table">
            <div className="hd-th">
              <span>Viewer</span>
              <span>Engagement score</span>
              <span>Documents</span>
              <span>Visits</span>
              <span>Last seen</span>
            </div>
            {s.viewerRows.map(row => (
              <div key={row.name} className="hd-tr hd-row-ease" style={{opacity: row.op, transform: `translateY(${row.ty}px)`}}>
                <span className="hd-who">
                  <span className="hd-avatar">{row.initials}</span>
                  <span>
                    <strong>{row.name}</strong>
                    <span>{row.role}</span>
                  </span>
                </span>
                <span>
                  <span className="hd-score" style={{background: row.chipBg, color: row.chipFg}}>{row.score}</span>
                  <span className="hd-likely">{row.likely}</span>
                </span>
                <span style={{fontSize: 13, fontWeight: 600}}>{row.docs}</span>
                <span style={{fontSize: 15, fontWeight: 600}}>{row.visits}</span>
                <span style={{fontSize: 13, color: '#475569'}}>{row.seen}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function Offers({s}) {
  return (
    <div className="hd-layer hd-offers hd-ease" style={{opacity: s.offersOn.op, transform: `translateY(${s.offersOn.ty}px)`}}>
      <div style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
        <div>
          <h3>Offers</h3>
          <p className="hd-subline">3 offers in · 3 awaiting your reply · highest price $1,825,000</p>
        </div>
        <div className="hd-sky-btn" style={{height: 40, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase'}}>
          Share summary
        </div>
      </div>
      <div className="hd-intel">
        <span className="hd-intel-label">
          <Icon size={14}><path d="M12 4l1.7 4.6L18 10l-4.3 1.4L12 16l-1.7-4.6L6 10l4.3-1.4z" /></Icon>
          Offer intelligence
          <span className="hd-intel-new">New</span>
        </span>
        <div className="hd-steps">
          <div className="hd-step"><i>1</i>Check completeness</div>
          <div className="hd-step" style={{background: s.nsBg}}><i>2</i>Generate net sheets</div>
          <div className="hd-step"><i>3</i>Analyze & compare</div>
        </div>
      </div>
      <div className="hd-offer-tools">
        <div className="hd-ghost" style={{height: 38, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase'}}>Add offer</div>
        <div style={{display: 'flex', gap: 10}}>
          <div className="hd-input" style={{width: 220, height: 38}}>Search by agent or term</div>
          <div className="hd-select" style={{height: 38}}>All statuses (3)</div>
          <div className="hd-select" style={{height: 38}}>Custom order</div>
        </div>
      </div>
      <div className="hd-offer-board">
        <div className="hd-offer-grid">
          {s.offerCols.map(offer => (
            <div key={offer.agent} className="hd-offer-col hd-row-ease" style={{opacity: offer.op, transform: `translateY(${offer.ty}px)`}}>
              <div className="hd-offer-top">
                <div className="hd-offer-id">
                  <span className="hd-offer-av">{offer.initials}</span>
                  <span className="hd-status">{offer.status}</span>
                </div>
                <h4>{offer.agent}</h4>
                <p>
                  {offer.email}
                  <br />
                  Phone: {offer.phone}
                  <br />
                  License: {offer.license}
                </p>
                <div className="hd-offer-stats">
                  <div><span>Price</span><b>{offer.price}</b></div>
                  <div>
                    <span>Est. net proceeds</span>
                    <b style={{color: '#15803d', opacity: offer.netOp, transition: 'opacity 350ms'}}>{offer.net}</b>
                  </div>
                  <div><span>Submitted on</span><em>{offer.sub}</em></div>
                  <div><span>Expires on</span><em>{offer.exp}</em></div>
                </div>
                <div className="hd-btn-line">View offer</div>
                <div className="hd-btn-sky">Respond to offer</div>
              </div>
              {[
                ['Financing', offer.fin],
                ['Contingencies', offer.cont],
                ['Timeline', offer.time],
              ].map(([title, rows]) => (
                <div className="hd-sec" key={title}>
                  <h5>{title}</h5>
                  <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                    {rows.map(row => (
                      <div className="hd-kv" key={row.k}>
                        <span style={{color: '#475569'}}>{row.k}</span>
                        <b>{row.v}</b>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="hd-sec">
                <h5>Special terms</h5>
                <div style={{fontSize: 13, lineHeight: 1.5}}>{offer.terms}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CreateModal({s}) {
  return (
    <div className="hd-modal" style={{opacity: s.modalOp}}>
      <div className="hd-create" style={{transform: `translateY(${s.modalTy}px)`}}>
        <div className="hd-create-h">
          Create a listing
          <Icon size={20}><path d="M6 6 18 18M18 6 6 18" /></Icon>
        </div>
        <div className="hd-create-body">
          <div className="hd-create-card">
            <div style={{display: 'flex', gap: 12}}>
              <span className="hd-sum-ico" style={{width: 36, height: 36, flex: '0 0 36px', background: '#e8f5ff', color: '#0f6da8'}}>
                <Icon size={18}><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M20 21H4" /></Icon>
              </span>
              <div>
                <div style={{fontSize: 15, fontWeight: 600}}>Pull details from an MLS listing</div>
                <div style={{fontSize: 13, color: '#737373', marginTop: 4, lineHeight: 1.5}}>Search for the property and we'll fill in the address, features, and photos.</div>
              </div>
            </div>
            <div className="hd-search">
              <div className="hd-search-field" style={{position: 'relative'}}>
                <span style={{position: 'absolute', left: 11, top: 11, color: '#94a3b8'}}>
                  <Icon size={16}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>
                </span>
                <span>{s.typed}</span>
                <span className="hd-caret" style={{opacity: s.caretOp}} />
                <span className="hd-ph" style={{opacity: s.placeholderOp}}>Search by address</span>
              </div>
              <div className="hd-results" style={{opacity: s.resultsOp, transform: `translateY(${s.resultsTy}px)`}}>
                <div className="hd-result" style={{background: s.resultBg}}>
                  <span className="hd-thumb" style={{width: 34, height: 34, backgroundImage: `url(${PHOTOS.main})`}} />
                  <span className="hd-result-copy">
                    <strong>123 Main Street, Anytown, CA 00000</strong>
                    <span>Active · $1,800,000 · 4 bd · 4 ba</span>
                  </span>
                  <Chevron />
                </div>
                <div className="hd-result" style={{borderBottom: 0}}>
                  <span className="hd-thumb" style={{width: 34, height: 34, backgroundImage: `url(${PHOTOS.elm})`}} />
                  <span className="hd-result-copy">
                    <strong>123 Main Court, Sampleville, CA 00000</strong>
                    <span>Sold · $2,150,000 · 5 bd · 4 ba</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="hd-hint">Not seeing your listing? We may not have access to your MLS feed.</div>
          </div>
          <div className="hd-create-card" style={{display: 'flex', alignItems: 'flex-start', gap: 12}}>
            <span className="hd-sum-ico" style={{width: 36, height: 36, flex: '0 0 36px', background: '#f1f5f9', color: '#475569'}}>
              <Icon size={18}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Icon>
            </span>
            <span>
              <strong style={{display: 'block', fontSize: 15}}>Enter details yourself</strong>
              <span style={{display: 'block', fontSize: 13, color: '#737373', marginTop: 4, lineHeight: 1.5}}>Add the address, features, and photos by hand. Use this if your property is not yet listed.</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function ShareModal({s}) {
  const share = s.share
  return (
    <div className="hd-modal" style={{opacity: share.op, zIndex: 30, display: 'block'}}>
      <div className="hd-share" style={{transform: `translateY(${share.ty}px)`}}>
        <div className="hd-share-h">
          Share package
          <Icon size={22}><path d="M18 6 6 18M6 6l12 12" /></Icon>
        </div>
        <div className="hd-share-body">
          <div>
            <div className="hd-field-label">Role</div>
            <div className="hd-role">Buyer agent</div>
          </div>
          <div className="hd-pair">
            <div>
              <div className="hd-field-label">Name</div>
              <div className="hd-field" style={{borderColor: share.nameBd}}>
                <span className="hd-ph" style={{opacity: share.namePh}}>Name</span>
                <span>{share.name}</span>
                <span className="hd-caret" style={{opacity: share.nameCaret}} />
              </div>
            </div>
            <div>
              <div className="hd-field-label">Email</div>
              <div className="hd-field" style={{borderColor: share.emailBd}}>
                <span className="hd-ph" style={{opacity: share.emailPh}}>Email</span>
                <span>{share.email}</span>
                <span className="hd-caret" style={{opacity: share.emailCaret}} />
              </div>
            </div>
          </div>
          <div style={{opacity: share.listOp, transition: 'opacity 250ms'}}>
            <div className="hd-field-label">Sharing with · {share.count}</div>
            {s.shareRows.map(row => (
              <div key={row.email} className="hd-recipient hd-row-ease" style={{opacity: row.op, transform: `translateY(${row.ty}px)`}}>
                <i>{row.ini}</i>
                <span style={{flex: 1}}>
                  <strong>{row.name}</strong>
                  <span>Buyer agent · {row.email}</span>
                </span>
              </div>
            ))}
          </div>
          <div>
            <div className="hd-field-label">Message</div>
            <div className="hd-msg">Add custom message</div>
          </div>
        </div>
        <div className="hd-share-foot">
          <div className="hd-ghost" style={{height: 44}}>Get share link</div>
          <div className="hd-share-go" style={{background: share.btnBg, transform: `translateY(${share.btnTy}px)`}}>Share package</div>
        </div>
      </div>
    </div>
  )
}

export default HeroDemo
